"use client";
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import "mapbox-gl/dist/mapbox-gl.css";
import HeaderBanner from "@packages/shared-components/common-utilities/header-banner/header-banner";
import HeaderBannerSkeleton from "@packages/shared-components/skeleton/institution-profile/header-banner-skeleton";
import Faqskeleton from "@packages/shared-components/skeleton/faqskeleton";
import TabViewComponents from "@packages/shared-components/common-utilities/tab-view/tabviewcomponents";
import FavFunctionalityWrapper from "@packages/shared-components/common-utilities/user-favourite/FavFunctionalityWrapper";
import UniversityOpenDaysSlider from "@packages/shared-components/common-utilities/slider/university-open-day/university-opendays-slider";
import { transformOpenDayItem } from "@packages/shared-components/common-utilities/slider/university-open-day/transform-open-day-item";
import ReviewthumbgallerysliderSkeleton from "@packages/shared-components/skeleton/reviewthumbgalleryslider-skeleton";
import PageViewLogging from "@packages/lib/utlils/pageviewlogging";
import { OPENDAYS_NON_ADVERTISER_DESCRIPTION, getAdvertiserDescription, OVERVIEW_TEXT, OVERVIEW_TITLE, FEATURED_TOURS_TITLE, LOCATION_TITLE, GALLERY_TITLE, NO_ADDRESS_TEXT } from "./opendays-constants";

const LocationMapCard = dynamic(
  () => import("@packages/shared-components/course-details/uni-info/LocationMapCard"),
  { ssr: false },
);
const VeppleClientWrapper = dynamic(
  () =>
    import("@packages/shared-components/common-utilities/vepple/vepple-wrapper"),
);
const ReviewThumbGallerySliderComponents = dynamic(
  () =>
    import("@packages/shared-components/common-utilities/slider/reviewthumbgalleryslidercomponents"),
  { loading: () => <ReviewthumbgallerysliderSkeleton /> },
);
const Faqcomponents = dynamic(
  () =>
    import("@packages/shared-components/common-utilities/faq/faqcomponents"),
  { loading: () => <Faqskeleton /> },
);
const ContentfulPreviewProvider = dynamic(
  () =>
    import("@packages/lib/contentful-preview/ContentfulLivePreviewProvider"),
);
const Subscribecomponents = dynamic(
  () =>
    import("@packages/shared-components/common-utilities/newsletter-and-subscription/subscribe-newsletter/subscribecomponents"),
);

interface UniversityOpenDayPageProps {
  btncount: number;
  responseData: any;
  children?: React.ReactNode;
  cityGuidedata?: any;
  openDaysUnivName?: string;
  commonGAAttrs?: any;
  commonOldGAAttrs?: any;
  csCommonData?: any;
  gaPageName?: string;
}

interface OpenDaysSectionProps {
  title: string;
  description?: string;
  cards: any[];
  hasAdvertiser?: boolean;
  isOnlineOpenDays?: boolean;
  hasVeppleSection?: boolean;
  heading?: string;
  institutionName?: string;
  institutionId?: string;
}

const OpenDaysSection = ({
  title,
  description,
  cards,
  hasAdvertiser,
  isOnlineOpenDays,
  hasVeppleSection,
  heading,
  institutionName,
  institutionId,
}: OpenDaysSectionProps) => {
  if (cards.length === 0) return null;
  return (
    <section className="bg-grey-50 py-[24px] md:py-[40px]">
      <div className="max-w-container mx-auto">
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[4px] px-[16px] md:px-[20px] xl:px-0">
            <div className="text-heading4 md:text-heading3 font-farro font-bold">{title}</div>
            {description && <p>{description}</p>}
          </div>
          <UniversityOpenDaysSlider
            openDayCards={cards as any}
            isAdvertiser={hasAdvertiser}
            isOnlineOpenDays={isOnlineOpenDays}
            hasVeppleSection={hasVeppleSection}
            heading={heading}
            institutionName={institutionName}
            institutionId={institutionId}
          />
        </div>
      </div>
    </section>
  );
};

const useMapbox = (location: any) => {
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;

    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let observer: IntersectionObserver | null = null;
    let isCancelled = false;

    const initMap = () => {
      if (isCancelled || !mapContainerRef.current) return;

      const container = mapContainerRef.current;

      const loadMapBox = async () => {
        if (isCancelled) return;
        const mapboxgl = (await import("mapbox-gl")).default;

        // Inject mapbox CSS if not already loaded
        if (!document.querySelector('link[href*="mapbox-gl"]')) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
          document.head.appendChild(link);
        }

        if (isCancelled) return;

        mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAP_BOX_TOKEN;
        const mapInstance = new mapboxgl.Map({
          container,
          style: "mapbox://styles/mapbox/streets-v12",
          zoom: 15,
          center: [location.longitude, location.latitude],
        });
        mapRef.current = mapInstance;

        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: false,
        }).setHTML(
          `<div class="relative px-3 py-2 text-sm font-bold text-black whitespace-nowrap font-sans">${location.venueName || NO_ADDRESS_TEXT}</div>`,
        );

        mapInstance.addControl(
          new mapboxgl.NavigationControl(),
          "bottom-right",
        );
        mapInstance.on("load", () => {
          new mapboxgl.Marker({ color: "#00BBFD" })
            .setLngLat([location.longitude, location.latitude])
            .addTo(mapInstance)
            .setPopup(popup);
        });
      };

      observer = new IntersectionObserver((entries) => {
        if (!mapRef.current && entries[0]?.isIntersecting) {
          observer?.unobserve(container);
          loadMapBox();
        }
      });

      observer.observe(container);
    };

    // Poll until the dynamic component mounts and attaches the ref
    const pollForContainer = () => {
      if (isCancelled) return;
      if (mapContainerRef.current) {
        initMap();
      } else {
        pollTimer = setTimeout(pollForContainer, 100);
      }
    };

    pollForContainer();

    return () => {
      isCancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      observer?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [location?.latitude, location?.longitude]);

  return mapContainerRef;
};

export const UniversityOpenDayPage = ({
  btncount,
  responseData,
  children,
  cityGuidedata,
  openDaysUnivName,
  commonGAAttrs = {},
  commonOldGAAttrs = {},
  csCommonData = {},
  gaPageName = "opendays_details",
}: UniversityOpenDayPageProps) => {
  const {
    uniInfo,
    location,
    enquiryDetails,
    openDays,
    onlineOpenDays,
    galleryDetails,
    veppleDetails,
    faqData,
    tabSectionList,
    isAdvertiser,
  } = responseData;

  const institutionName = uniInfo?.institutionName || "";
  const institutionId = uniInfo?.institutionId || "";

  const mapContainerRef = useMapbox(location);

  // Scroll to #vepple hash target after dynamic component loads
  useEffect(() => {
    if (window.location.hash !== "#vepple") return;

    // Wait for element to render, then scroll with CSS scroll-margin handling it
    const scrollToVepple = () => {
      const el = document.getElementById("vepple");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return true;
      }
      return false;
    };

    if (!scrollToVepple()) {
      const observer = new MutationObserver((_, obs) => {
        if (scrollToVepple()) obs.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 5000);
      return () => observer.disconnect();
    }
  }, []);

  const openDayCards = useMemo(
    () => (openDays ?? []).map(transformOpenDayItem),
    [openDays],
  );
  const onlineOpenDayCards = useMemo(
    () => (onlineOpenDays ?? []).map(transformOpenDayItem),
    [onlineOpenDays],
  );

  const totalOpenDays = openDayCards.length + onlineOpenDayCards.length;

  return (
    <div>
      <PageViewLogging
        pageNameLocal={gaPageName}
        gaData={{
          ...commonGAAttrs,
        }}
        csData={{...csCommonData}}
        gaoldData={{...commonOldGAAttrs}}
      />
      <FavFunctionalityWrapper
        commonGAAttrs={commonGAAttrs}
        cSCommonData={csCommonData}
        commonOldGAAttrs={commonOldGAAttrs}
      >
        <Suspense fallback={<HeaderBannerSkeleton />}>
          <HeaderBanner
            h1TitleFormat={"[COLLEGE NAME] Open Days"}
            uniInfo={uniInfo}
            enquiryDetails={enquiryDetails}
            isClearing={false}
            isVirtualTourAvailable={
              uniInfo?.virtualTourFlag?.toLowerCase() === "y" || !!veppleDetails?.veppelId
            }
            gallery={galleryDetails}
            hasRecommendedUni={!isAdvertiser}
            commonGAAttrs={{ ...commonGAAttrs, page_section: "open_days_header" }}
            csCommonData={{...csCommonData, sectionName:"Open Days Header"}}
            btncount={btncount}
            isPostgraduate={false}
            defaultFragmentId={tabSectionList?.[0]?.sectionId}
            commonOldGAAttrs={commonOldGAAttrs}
            isIpPage={false}
            isOpenDayPage={true}
            hasUgCourses={false}
          />
        </Suspense>
        <TabViewComponents tabSectionList={tabSectionList} commonGAAttrs={commonGAAttrs} />
      </FavFunctionalityWrapper>

      {isAdvertiser === false && (
        <OpenDaysSection
          title={`Upcoming open days at ${openDaysUnivName || institutionName}`}
          description={OPENDAYS_NON_ADVERTISER_DESCRIPTION}
          cards={openDayCards}
          hasVeppleSection={!!veppleDetails?.veppelId}
          heading={`Upcoming open days at ${openDaysUnivName || institutionName}`}
          institutionName={institutionName}
          institutionId={institutionId}
        />
      )}

      {isAdvertiser && (
        <>
          <OpenDaysSection
            title={`Upcoming open days at ${openDaysUnivName || institutionName}`}
            description={getAdvertiserDescription(totalOpenDays, openDaysUnivName || institutionName)}
            cards={openDayCards}
            hasAdvertiser
            hasVeppleSection={!!veppleDetails?.veppelId}
            heading={`Upcoming open days at ${openDaysUnivName || institutionName}`}
            institutionName={institutionName}
            institutionId={institutionId}
          />
          <OpenDaysSection
            title={`${openDaysUnivName || institutionName} Anytime Open Days`}
            cards={onlineOpenDayCards}
            hasAdvertiser
            isOnlineOpenDays
            hasVeppleSection={!!veppleDetails?.veppelId}
            heading={`${openDaysUnivName || institutionName} Open Days`}
            institutionName={institutionName}
            institutionId={institutionId}
          />
        </>
      )}

      <section className="bg-white py-[40px] px-[16px] md:px-[20px] xl:px-0">
        <div className="max-w-container mx-auto">
          <div className="text-heading4 md:text-heading3 font-farro font-bold">
            {OVERVIEW_TITLE}
          </div>
          <p className="pt-[4px] md:pt-[16px]">
            {OVERVIEW_TEXT}
          </p>
        </div>
      </section>

      {veppleDetails?.veppelId && veppleDetails?.veppleValue && (
        <section id="vepple" className="bg-grey-50 py-[32px] md:py-[40px] px-[16px] md:px-[20px] xl:px-0 scroll-mt-[100px]">
          <div id="vepple-section" className="max-w-container mx-auto">
            <div className="flex flex-col gap-[16px]">
              <div className="h3">{FEATURED_TOURS_TITLE}</div>
              <VeppleClientWrapper
                uniInfo={uniInfo}
                enquiry={enquiryDetails}
                id={veppleDetails.veppelId}
                url={veppleDetails.veppleValue}
                page_section={'Featured tours'}
              />
            </div>
          </div>
        </section>
      )}

      {location?.latitude && location?.longitude && (
        <section className="bg-grey-50 py-[32px] md:py-[40px] px-[16px] md:px-[20px] xl:px-0">
          <div className="max-w-container mx-auto">
            <div className="text-heading4 md:text-heading3 font-farro font-bold pb-[16px]">
            {LOCATION_TITLE}
            </div>
            <LocationMapCard
              location={location}
              institutionName={institutionName}
              cityGuidedata={cityGuidedata}
              mapContainerRef={mapContainerRef}
              isUniOpenDayPage={true}
            />
          </div>
        </section>
      )}

      {Array.isArray(galleryDetails) && galleryDetails.length > 0 && (
        <section className="bg-white pt-10 md:px-[16px] lg:px-0">
          <div className="max-w-container mx-auto">
            <div className="hidden md:block text-heading4 md:text-heading3 font-farro font-bold mb-4">
              {GALLERY_TITLE}
            </div>
            <ReviewThumbGallerySliderComponents
              showGallery={true}
              galleryItems={galleryDetails}
              isPostgraduate={false}
              commonGAAttrs={{...commonGAAttrs,click_text:'na'}}
              isFullWidth
              isOpenDayPage
            />
          </div>
        </section>
      )}

      <div className="bg-white" data-faq="faq_section" data-label="faqsection_viewed">
        <Faqcomponents componentData={faqData} isOpenDayPage />
      </div>

      {children}

      <ContentfulPreviewProvider
        locale="en-GB"
        enableInspectorMode={false}
        enableLiveUpdates={false}
        debugMode={false}
      >
        <Subscribecomponents iscontentPreview={false} pageGrouping={""} />
      </ContentfulPreviewProvider>
    </div>
  );
};
